import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, Alert, theme } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { authApi, type LoginCredentials } from '@/services/api/auth.api';
import { FIREBASE_AUTH_ENABLED } from '@/services/api/authProvider';
import { useFormStyles } from '@/styles/formStyles';
import { ThemeProvider } from '@/contexts/ThemeContext';

const { Title } = Typography;

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFinish = async (values: LoginCredentials) => {
    setLoading(true);
    setError(null);

    try {
      // AUTH-FB — THIS ONE CONDITION IS WRITTEN AS A RAW `import.meta.env`
      // COMPARISON ON PURPOSE, and it is the only place in `src/` that reads
      // VITE_AUTH_PROVIDER besides services/api/authProvider.ts.
      //
      // Vite substitutes the literal HERE, so a non-Firebase build compiles
      // to `if ("local" === "firebase")`, which Rollup deletes along with the
      // dynamic import and the whole Firebase SDK. Reading the imported
      // `FIREBASE_AUTH_ENABLED` constant instead LOOKS equivalent and is not:
      // it was tried first, and Rollup still emitted a 500 KB
      // `firebaseAuth.api-*.js` chunk into the demo artifact —
      // `scripts/assert-firebase-free-artifact.cjs` caught it, which is why
      // that gate exists and why this comment does. Do not "tidy" this line.
      if (import.meta.env.VITE_AUTH_PROVIDER === 'firebase') {
        const { loginWithFirebase } = await import('@/services/api/firebaseAuth.api');
        // The identifier field carries an email in this mode (see the input's
        // placeholder/autoComplete below).
        await loginWithFirebase({ email: values.username, password: values.password });
      } else {
        await authApi.login(values);
      }
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      navigate(from || '/auth/vendors', { replace: true });
    } catch (err) {
      // A refused Firebase login already resolved itself to an i18n key
      // (too-many-attempts, not-provisioned, stale sign-in, provider down);
      // anything else stays the generic credential message.
      const messageKey = (err as { messageKey?: unknown } | null)?.messageKey;
      setError(t(typeof messageKey === 'string' ? messageKey : 'login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: token.colorBgLayout,
        padding: token.paddingLG,
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: token.boxShadowSecondary,
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: token.marginLG }}>
          <img
            src="/logo.svg"
            alt=""
            width={48}
            height={46}
            style={{ display: 'block', margin: '0 auto', marginBottom: token.marginSM }}
          />
          <Title level={3} style={{ marginBottom: token.marginXS, color: token.colorPrimary }}>
            {t('app.title')}
          </Title>
          <Typography.Text type="secondary">{t('login.subtitle')}</Typography.Text>
        </div>

        {error && (
          <Alert message={error} type="error" showIcon style={{ marginBottom: token.marginMD }} />
        )}

        <Form name="login" onFinish={onFinish} layout="vertical" size="large" requiredMark={false}>
          <Form.Item
            name="username"
            rules={[
              {
                required: true,
                message: FIREBASE_AUTH_ENABLED
                  ? t('login.emailRequired')
                  : t('login.usernameRequired'),
              },
            ]}
          >
            <Input
              prefix={<UserOutlined style={{ color: token.colorTextSecondary }} />}
              // AUTH-FB: Firebase signs in by email address, so the field asks
              // for one. The form key stays `username` — the password leg and
              // every existing test drive that name, and the two modes must
              // not fork the form's shape over a label.
              placeholder={FIREBASE_AUTH_ENABLED ? t('login.email') : t('login.username')}
              style={formStyles.inputStyle}
              autoComplete={FIREBASE_AUTH_ENABLED ? 'email' : 'username'}
              type={FIREBASE_AUTH_ENABLED ? 'email' : 'text'}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: t('login.passwordRequired') }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: token.colorTextSecondary }} />}
              placeholder={t('login.password')}
              style={formStyles.inputStyle}
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={formStyles.modalButtonStyle}
            >
              {t('login.submit')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

// Wrap with ThemeProvider since login page is outside MainLayout
const LoginPage = () => {
  return (
    <ThemeProvider defaultMode="light">
      <LoginForm />
    </ThemeProvider>
  );
};

export default LoginPage;
